// ZodiacPersonalityTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { UserCircle } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import { UKV2TextInput, UKV2SubmitButton } from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2ZodiacPersonalityData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Zodiac Personality";

// "?sign=aries&topic=personal_questions" arrives here when the user clicked
// a sign on the Zodiac Homepage grid (see ZodiacHomeScreen.tsx) — per
// Uk.txt's ZodiacHome_UK_v2 spec ("click_behavior": "open_topic_room"). The
// sign/topic are folded into the request as framing context; the backend
// still determines the actual sun sign from the DOB it computes the chart
// from, so a mismatched click (e.g. an Aries born date routed via a Taurus
// click) never produces a wrong reading — it just loses the topic framing.
function topicLabelFromSlug(topic: string | null): string | null {
  if (!topic) return null;
  return topic
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Colors must be read INSIDE AstriaUKV2Shell's subtree — the shell mounts
// its own AstriaUKV2ThemeProvider internally, and JSX passed as `children`
// is only rendered (hooks and all) once React mounts it there. Calling
// useUKV2Colors() in the parent tab component instead (as this file
// originally did) throws "must be used within an AstriaUKV2ThemeProvider",
// because that call happens during the PARENT's render, before the
// provider exists anywhere in the tree. Keeping all colors-dependent
// content in these small inner components — rendered as children, not
// evaluated inline — is what makes the timing correct.

const ResultView: React.FC<{
  data?: AstriaUKV2ZodiacPersonalityData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<UserCircle className="w-5 h-5" />}
          title={t("ukv2.tabs.zodiacPersonality.label")}
          subtitle={t("ukv2.zodiac.subtitle")}
          shareText={`${data.core_trait}\n\n${data.closing}`}
        >
          <AstriaUKV2FieldBlock label={t("ukv2.field.coreTrait")} value={data.core_trait} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.everydayStyle")} value={data.everyday_style} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.whereItServes")} value={data.where_it_serves_you} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.whereItTrips")} value={data.where_it_trips_you_up} />
          <AstriaUKV2FieldBlock label={t("ukv2.common.closing")} value={data.closing} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("ukv2.common.startNewReading")}
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<UserCircle className="w-5 h-5" />} title={t("ukv2.tabs.zodiacPersonality.label")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (value: string) => void;
  topicLabel: string | null;
  onSubmit: () => void;
}> = ({ dob, setDob, topicLabel, onSubmit }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("ukv2.tabs.zodiacPersonality.label")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("ukv2.zodiac.intro")}
        </p>
        {topicLabel && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginTop: 10,
              padding: "5px 12px",
              borderRadius: 999,
              border: `1px solid ${colors.cardBorder}`,
              background: colors.section,
              color: colors.accent,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            ✦ {topicLabel}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <UKV2TextInput value={dob} onChange={setDob} placeholder={t("ukv2.common.dobPlaceholder")} />
      </div>

      <UKV2SubmitButton onClick={onSubmit} disabled={!dob.trim()} label={t("ukv2.zodiac.submit")} />
    </>
  );
};

const ZodiacPersonalityTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [searchParams] = useSearchParams();
  const topicLabel = topicLabelFromSlug(searchParams.get("topic"));

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(
      `Give me my Zodiac Personality read.${topicLabel ? ` I'm especially curious about ${topicLabel.toLowerCase()}.` : ""} My Date of Birth: ${dob.trim()}.`,
    );

  const data = message?.astriaUKV2Data as AstriaUKV2ZodiacPersonalityData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="zodiac-personality">
        {loading && <UKV2LoadingState label={t("ukv2.loading.zodiac")} />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="zodiac-personality">
      <IntroForm
        dob={dob}
        setDob={setDob}
        topicLabel={
          topicLabel
            ? t(`ukv2.topics.${searchParams.get("topic")}`, { defaultValue: topicLabel })
            : null
        }
        onSubmit={handleSubmit}
      />
    </AstriaUKV2Shell>
  );
};

export default ZodiacPersonalityTab;
